import React, { useEffect, useMemo, useRef, useState } from 'react';

type SpawnWindow = {
  time: string;
  durationHours: number;
};

type NextSpawn = {
  startsAt: Date;
  timeLabel: string;
  dayLabel: string;
};
const spawnWindows: SpawnWindow[] = [
  { time: '00:00', durationHours: 3 },
  { time: '06:00', durationHours: 3 },
  { time: '12:00', durationHours: 3 },
  { time: '18:00', durationHours: 3 },
];

const rules = [
  'Spawns on the World Map every Monday and Thursday at 00:00, 06:00, 12:00, and 18:00 (server time) and stays for 3 hours.',
  'Commanders with bases at level 8+ can send troops; unlimited challenge attempts per day.',
  'No rally attacks allowed. Festival events/Black Market Challenge can grant up to 5 extra drop opportunities.',
  'Tank heroes deal extra damage to Wanted Boss.',
];

const rewards = [
  'Reach the required number of challenges to claim milestone rewards.',
  'On challenge days, your best single-hit damage sets your ranking. Higher single damage = better rewards.',
  'Goals rewards unlock as server time progresses; each can be claimed once.',
];

const competitionNote =
  'Competition uses total damage from the top 200 commanders in each warzone; the warzone with higher total damage wins.';

const WantedBoss: React.FC = () => {
  const cardsRef = useRef<HTMLDivElement[]>([]);
  const [serverTime, setServerTime] = useState<Date>(new Date());
  const [nextSpawn, setNextSpawn] = useState<NextSpawn | null>(null);
  const spawnTimes = useMemo(() => spawnWindows.map((s) => s.time), []);
  const spawnDays = useMemo(() => [1, 4], []); // Monday=1, Thursday=4

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -40px 0px' }
    );

    cardsRef.current.forEach((card) => {
      if (card) {
        card.classList.add('fade-in-ready');
        observer.observe(card);
      }
    });

    return () => {
      cardsRef.current.forEach((card) => {
        if (card) observer.unobserve(card);
      });
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      setServerTime(now);

      const today = now.getUTCDay(); // 0=Sun

      const findNext = (): NextSpawn => {
        for (let dayOffset = 0; dayOffset <= 7; dayOffset += 1) {
          const candidate = new Date(now);
          candidate.setUTCDate(now.getUTCDate() + dayOffset);
          const candidateDay = (today + dayOffset) % 7;

          const normalizedDay = candidateDay === 0 ? 7 : candidateDay; // treat Sunday as 7
          if (!spawnDays.includes(normalizedDay)) continue;

          for (const time of spawnTimes) {
            const [h, m] = time.split(':').map(Number);
            const slotDate = new Date(candidate);
            slotDate.setUTCHours(h, m, 0, 0);
            if (slotDate > now) {
              return {
                startsAt: slotDate,
                timeLabel: time,
                dayLabel: slotDate.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' }),
              };
            }
          }
        }

        // fallback: next Monday 00:00
        const next = new Date(now);
        const daysUntilMonday = (8 - today) % 7 || 7;
        next.setUTCDate(now.getUTCDate() + daysUntilMonday);
        next.setUTCHours(0, 0, 0, 0);
        return {
          startsAt: next,
          timeLabel: '00:00',
          dayLabel: next.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' }),
        };
      };

      setNextSpawn(findNext());
    }, 1000);

    return () => clearInterval(interval);
  }, [spawnDays, spawnTimes]);

  const formatTime = (date: Date): string =>
    date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'UTC',
    });

  const formatCountdown = (target: Date): string => {
    const diffMs = target.getTime() - serverTime.getTime();
    if (diffMs <= 0) return 'Spawning now';
    const totalSeconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  };

  return (
    <section id="wanted-boss" className="section">
      <div className="container">
        <h2 className="section-title">Wanted Boss Schedule & Rules</h2>
        <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-text-secondary mb-6">
          <div className="bg-bg-card border border-border rounded-xl px-5 py-4 text-center shadow-glow">
            <div className="text-sm uppercase tracking-wide text-text-muted">Server Time</div>
            <div className="font-orbitron text-2xl text-primary-light">{formatTime(serverTime)}</div>
          </div>
          {nextSpawn && (
            <div className="bg-bg-card border border-border rounded-xl px-5 py-4 text-center shadow-glow">
              <div className="text-sm uppercase tracking-wide text-text-muted">Next Spawn</div>
              <div className="font-orbitron text-lg text-primary-light">
                {nextSpawn.dayLabel} @ {nextSpawn.timeLabel}
              </div>
              <div className="text-text-secondary text-sm mt-1">
                Starts in {formatCountdown(nextSpawn.startsAt)}
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-bg-card border border-border rounded-xl p-6 shadow-glow" ref={(el) => { if (el) cardsRef.current[0] = el; }}>
            <h3 className="font-orbitron text-xl text-primary-light mb-3">Spawn Times</h3>
            <p className="text-text-secondary mb-4">Appears every Monday and Thursday (server time).</p>
            <div className="divide-y divide-border">
              {spawnWindows.map((win) => (
                <div key={win.time} className="flex items-center justify-between py-3">
                  <span className="font-orbitron text-lg text-text-primary">{win.time}</span>
                  <span className="text-text-secondary text-sm">{win.durationHours}h duration</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-bg-card border border-border rounded-xl p-6" ref={(el) => { if (el) cardsRef.current[1] = el; }}>
            <h3 className="font-orbitron text-xl text-primary-light mb-3">Rules</h3>
            <ul className="list-none space-y-3 text-text-secondary">
              {rules.map((rule, idx) => (
                <li key={idx} className="flex gap-2 items-start">
                  <span className="text-primary">•</span>
                  <span>{rule}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 p-3 rounded-lg bg-bg-darker border border-border text-sm text-text-secondary">
              Tank heroes deal <span className="text-primary-light font-semibold">50% extra damage</span> on Wanted Boss.
            </div>
          </div>

          <div className="bg-bg-card border border-border rounded-xl p-6" ref={(el) => { if (el) cardsRef.current[2] = el; }}>
            <h3 className="font-orbitron text-xl text-primary-light mb-3">Rewards</h3>
            <ul className="list-none space-y-3 text-text-secondary">
              {rewards.map((rule, idx) => (
                <li key={idx} className="flex gap-2 items-start">
                  <span className="text-primary">•</span>
                  <span>{rule}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 p-3 rounded-lg bg-bg-darker border border-border text-sm text-text-secondary">
              {competitionNote}
            </div>
          </div>
        </div>

        <div className="bg-bg-card border border-border rounded-xl p-6 mt-8">
          <h3 className="font-orbitron text-xl text-primary-light mb-3">Tips to Contribute</h3>
          <ul className="list-none space-y-3 text-text-secondary">
            <li className="flex gap-2 items-start">
              <span className="text-primary">•</span>
              <span>Coordinate with the alliance to hit Wanted Boss at spawn; the boss lasts 3 hours per window.</span>
            </li>
            <li className="flex gap-2 items-start">
              <span className="text-primary">•</span>
              <span>Use your strongest tank heroes for the 50% bonus damage.</span>
            </li>
            <li className="flex gap-2 items-start">
              <span className="text-primary">•</span>
              <span>Chase your best single-hit damage early; it sets your ranking for the day.</span>
            </li>
            <li className="flex gap-2 items-start">
              <span className="text-primary">•</span>
              <span>Hit the challenge milestones to secure guaranteed rewards, then push ranking if time remains.</span>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
};

export default WantedBoss;





